import {
    AttachmentType,
    getScope,
    getScopeCertificateOfInsurance,
    getScopeOverview,
    getScopeTopicsWithClauses,
    getSignedScopeDocument,
    ScopeStatus,
} from "@/api";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { getClauseHighlightColor, HOME_ORG_PATH } from "@/config/constants";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { trackScopeViewed } from "@/lib/analytics";
import { Stack } from "@mui/material";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { ChatSheet } from "@/components/assistant-ui/chat-sheet";
import { PdfViewer } from "./components/pdf/PdfViewer";
import { ScopeDetails } from "./components/scope-details/ScopeDetails";
import { ScopeOverview } from "./components/scope-overview/ScopeOverview";
import { ScopeFailedState } from "./components/ScopeFailedState";
import { ScopeProcessingState } from "./components/ScopeProcessingState";
import { ScopeToolbar } from "./components/ScopeToolbar";
import {
    FoundToggleState,
    IGroupedScopeClause,
    IScopeClauseWithTopic,
    ScopeDetailsTab,
    ScopeViewType,
} from "./types";

/**
 * The Scope details page
 */
const Scope = () => {
    const { scopeId } = useParams();
    const [searchParams] = useSearchParams();
    const { setBreadcrumbs } = useBreadcrumb();

    useDocumentTitle("Scope");

    // Navigation and UI state
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const [pdfViewerLoaded, setPdfViewerLoaded] = useState(false);
    const [chatDrawerOpen, setChatDrawerOpen] = useState(false);

    // PDF page tracking for custom field defaults
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const handlePageChange = useCallback((pageIndex: number) => {
        const next = pageIndex + 1;
        setCurrentPage((prev) => (prev === next ? prev : next));
    }, []);

    const handleTotalPages = useCallback((numPages: number) => {
        setTotalPages(numPages);
    }, []);

    // Tab and toggle states
    const [tab, setTab] = useState<ScopeDetailsTab>(ScopeDetailsTab.FIELDS);
    const [fieldsFoundToggle, setFieldsFoundToggle] =
        useState<FoundToggleState>(FoundToggleState.FOUND);
    const [clausesFoundToggle, setClausesFoundToggle] =
        useState<FoundToggleState>(FoundToggleState.FOUND);

    // Search and filter states
    const [fieldsSearch, setFieldsSearch] = useState<string>("");
    const [clausesSearch, setClausesSearch] = useState<string>("");
    const [, setFilteredClausesCount] = useState<number>();

    // Clause expansion states
    const [expandedFoundClause, setExpandedFoundClause] = useState<
        string | false
    >(false);

    // Get the current search term based on active tab
    const currentSearch =
        tab === ScopeDetailsTab.FIELDS ? fieldsSearch : clausesSearch;
    const setCurrentSearch =
        tab === ScopeDetailsTab.FIELDS ? setFieldsSearch : setClausesSearch;

    // Fetch scope data
    const { data: scope } = useSuspenseQuery({
        queryKey: ["frontend-scope", scopeId],
        queryFn: () => getScope(parseInt(scopeId!)),
    });

    const [viewType, setViewType] = useState<ScopeViewType>(
        ScopeViewType.OVERVIEW
    );

    const { data: signedScopeDocument, isLoading: scopeDocumentLoading } =
        useQuery({
            queryKey: ["frontend-scopeDocumentUrl", scope.id],
            queryFn: () => getSignedScopeDocument(scope.id),
        });

    const { data: topicsWithClauses } = useQuery({
        queryKey: ["scope-topics-with-clauses", scope.id],
        queryFn: () => getScopeTopicsWithClauses(scope.id),
        enabled: !!scope,
    });

    const { data: coi } = useQuery({
        queryKey: ["frontend-scope-coi", scope.id],
        queryFn: () => getScopeCertificateOfInsurance(scope.id),
        enabled: !!scope,
    });

    const { data: scopeOverview, isPending: isOverviewPending } = useQuery({
        queryKey: ["frontend-scope-overview", scope.id],
        queryFn: () => getScopeOverview(scope.id),
        enabled: !!scope,
    });

    // Extract all clauses from V2 topics data with topic context preserved
    const v2Clauses = useMemo((): IScopeClauseWithTopic[] => {
        if (!topicsWithClauses?.topics) return [];

        const allClauses: IScopeClauseWithTopic[] = [];
        topicsWithClauses.topics.forEach((topic) => {
            topic.sections.forEach((section) => {
                section.clauses.forEach((clause) => {
                    // Extend clause with topic info for proper multi-topic handling
                    allClauses.push({
                        ...clause,
                        scopeTopicId: topic.id,
                        scopeTopicName: topic.name,
                    });
                });
            });
        });
        return allClauses;
    }, [topicsWithClauses]);

    const scopeClauses = useMemo(() => {
        if (v2Clauses.length === 0) return [];

        const sorted = [...v2Clauses].sort((a, b) => {
            const aPageIndex = a.highlight_areas?.[0]?.page_index ?? 0;
            const bPageIndex = b.highlight_areas?.[0]?.page_index ?? 0;
            return aPageIndex - bPageIndex;
        });

        const groupedClauses = sorted.reduce((map, scopeClause) => {
            const clauseId = scopeClause.clause.id;

            if (!map.has(clauseId)) {
                map.set(clauseId, {
                    clause: scopeClause.clause,
                    scopeClauses: [],
                    highlightColor: getClauseHighlightColor(clauseId),
                });
            }

            map.get(clauseId)!.scopeClauses.push(scopeClause);
            return map;
        }, new Map<number, IGroupedScopeClause>());

        return Array.from(groupedClauses.values());
    }, [v2Clauses]);

    useEffect(() => {
        setBreadcrumbs([
            { label: "Home", path: HOME_ORG_PATH },
            {
                label: "Scopes",
                path: "/organizations/scopes",
                openInNewTab: true,
            },
            { label: scope.document_title },
        ]);
    }, [scope, setBreadcrumbs]);

    // is_v2 and status intentionally excluded from deps: a status flip on the same scope is not a fresh view.
    useEffect(() => {
        trackScopeViewed({
            scope_id: scope.id,
            is_v2: scope.is_v2,
            status: scope.status,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope.id]);

    useEffect(() => {
        setFilteredClausesCount(scopeClauses.length);
    }, [scopeClauses]);

    // Track clause param handling state
    const [clauseParamState, setClauseParamState] = useState<{
        hasSetState: boolean;
        hasJumpedToPdf: boolean;
        scopeClauseId: number | null;
    }>({ hasSetState: false, hasJumpedToPdf: false, scopeClauseId: null });

    // Track topic param handling state
    const [topicParamHandled, setTopicParamHandled] = useState(false);
    const [scrollToTopicId, setScrollToTopicId] = useState<number | null>(null);

    // handles 'clause' URL param to navigate to clauses tab and select the clause
    useEffect(() => {
        const paramClauseId = parseInt(searchParams.get("clause") ?? "");
        if (!paramClauseId || scopeClauses.length === 0) return;

        // Find the grouped clause that matches the clause_id from URL
        const matchingGroup = scopeClauses.find(
            (group) => group.clause.id === paramClauseId
        );

        if (!matchingGroup || matchingGroup.scopeClauses.length === 0) return;

        // Prefer found clauses, fall back to not found only if no found instances exist
        const foundScopeClause = matchingGroup.scopeClauses.find(
            (sc) => sc.found
        );
        const targetScopeClause =
            foundScopeClause || matchingGroup.scopeClauses[0];

        // Set UI state only once
        if (!clauseParamState.hasSetState) {
            setClausesFoundToggle(
                targetScopeClause.found
                    ? FoundToggleState.FOUND
                    : FoundToggleState.NOT_FOUND
            );
            setTab(ScopeDetailsTab.CLAUSES);
            setExpandedFoundClause(`panel${targetScopeClause.id}`);
            setClauseParamState((prev) => ({
                ...prev,
                hasSetState: true,
                scopeClauseId: targetScopeClause.id,
            }));
        }

        // Jump to PDF once it's loaded (only once)
        if (
            pdfViewerLoaded &&
            !clauseParamState.hasJumpedToPdf &&
            targetScopeClause.highlight_areas.length > 0
        ) {
            pageNavigationPluginInstance.jumpToPage(
                targetScopeClause.highlight_areas[0].page_index
            );
            setClauseParamState((prev) => ({ ...prev, hasJumpedToPdf: true }));
        }
    }, [
        searchParams,
        scopeClauses,
        setClausesFoundToggle,
        pdfViewerLoaded,
        pageNavigationPluginInstance,
        clauseParamState.hasSetState,
        clauseParamState.hasJumpedToPdf,
    ]);

    // Handle 'topic' URL param to navigate to topic details (from Excel report links)
    useEffect(() => {
        const paramTopicId = parseInt(searchParams.get("topic") ?? "");
        if (!paramTopicId || topicParamHandled) return;

        // Switch to Details view and Clauses tab
        setViewType(ScopeViewType.DETAILS);
        setTab(ScopeDetailsTab.CLAUSES);

        // Set the topic ID to scroll to (will be handled by ScopeTopicsV2)
        setScrollToTopicId(paramTopicId);

        setTopicParamHandled(true);
    }, [searchParams, topicParamHandled]);

    useEffect(() => {
        if (!scope.field_extraction_enabled) {
            setTab(ScopeDetailsTab.CLAUSES);
        }
    }, [scope]);

    // Automatically set view type to Overview when overview data is available
    // (but not if there's a topic query param - that should go to Details)
    useEffect(() => {
        const hasTopicParam = searchParams.has("topic");
        const paramClauseId = parseInt(searchParams.get("clause") ?? "");

        if (scopeOverview && !hasTopicParam && !paramClauseId) {
            setViewType(ScopeViewType.OVERVIEW);
        } else if (scopeOverview && paramClauseId) {
            setViewType(ScopeViewType.DETAILS);
        }
    }, [scopeOverview, searchParams]);

    // Handler for "View Details" from TopicCard - switches to Details view
    const handleViewTopicDetails = useCallback((topicId: number) => {
        setViewType(ScopeViewType.DETAILS);
        setTab(ScopeDetailsTab.CLAUSES);
        // Set the topic ID to scroll to (will be handled by ScopeTopicsV2)
        setScrollToTopicId(topicId);
    }, []);

    // Redirect if scopeId is missing
    if (!scopeId) {
        return <Navigate to="/organizations" />;
    }

    return (
        <FrontendPageContent className="flex max-h-[calc(100vh-var(--top-offset))]! flex-col">
            <ScopeToolbar
                scope={scope}
                signedScopeDocument={signedScopeDocument}
                onChatOpen={() => setChatDrawerOpen(true)}
                viewType={viewType}
                setViewType={setViewType}
                isProcessing={
                    scope.status === ScopeStatus.PROCESSING ||
                    scope.status === ScopeStatus.PENDING
                }
                isFailed={scope.status === ScopeStatus.FAILED}
            />

            <Stack
                direction="row"
                justifyContent="space-between"
                flex={1}
                bgcolor="#F5F7F9"
                sx={{ overflow: "hidden" }}
            >
                {/* Scope Processing State */}
                {(scope.status === ScopeStatus.PROCESSING ||
                    scope.status === ScopeStatus.PENDING) && (
                    <ScopeProcessingState scope={scope} />
                )}

                {/* Scope Failed State */}
                {scope.status === ScopeStatus.FAILED && (
                    <ScopeFailedState scope={scope} />
                )}

                {/* Scope Overview and Details */}
                {scope.status === ScopeStatus.COMPLETE &&
                    (viewType === ScopeViewType.OVERVIEW ? (
                        <ScopeOverview
                            overview={scopeOverview}
                            isLoading={isOverviewPending}
                            onViewDetails={handleViewTopicDetails}
                        />
                    ) : (
                        <>
                            <ScopeDetails
                                tab={tab}
                                setTab={setTab}
                                fieldsFoundToggle={fieldsFoundToggle}
                                setFieldsFoundToggle={setFieldsFoundToggle}
                                clausesFoundToggle={clausesFoundToggle}
                                setClausesFoundToggle={setClausesFoundToggle}
                                setFilteredClausesCount={
                                    setFilteredClausesCount
                                }
                                search={currentSearch}
                                setSearch={setCurrentSearch}
                                scope={scope}
                                coi={coi}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                                pdfViewerLoaded={pdfViewerLoaded}
                                expandedFoundClause={expandedFoundClause}
                                setExpandedFoundClause={setExpandedFoundClause}
                                scrollToTopicId={scrollToTopicId}
                                currentPage={currentPage}
                                totalPages={totalPages}
                            />
                            <PdfViewer
                                signedScopeDocument={signedScopeDocument}
                                signedScopeDocumentLoading={
                                    scopeDocumentLoading
                                }
                                setTab={setTab}
                                setClausesFoundToggle={setClausesFoundToggle}
                                groupedScopeClauses={scopeClauses}
                                setExpandedFoundClause={setExpandedFoundClause}
                                expandedFoundClause={expandedFoundClause}
                                pageNavigationPluginInstance={
                                    pageNavigationPluginInstance
                                }
                                setPdfViewerLoaded={setPdfViewerLoaded}
                                onPageChange={handlePageChange}
                                onTotalPages={handleTotalPages}
                            />
                        </>
                    ))}
            </Stack>

            <ChatSheet
                attachment={{
                    id: scope.id,
                    name: scope.document_title,
                    type: AttachmentType.Scope,
                }}
                open={chatDrawerOpen}
                onClose={() => setChatDrawerOpen(false)}
            />
        </FrontendPageContent>
    );
};

export default Scope;
